/**
 * Types pour le système de widgets du dashboard - Style Notion
 * Modulable, drag & drop, personnalisable
 */

export type WidgetType = 
  | 'kpi'              // Indicateur clé unique
  | 'balance'          // Résumé bilan
  | 'pnl'              // Compte de résultat
  | 'cash'             // Position trésorerie
  | 'tax'              // Situation fiscale
  | 'ratio'            // Ratio financier
  | 'account'          // Suivi de compte
  | 'chart'            // Graphique
  | 'metrics'          // Métriques SYSCOHADA
  | 'credit_score';    // Cote de crédit Wanzo (ML)

export type WidgetSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface WidgetPosition {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type KPIMetric = 
  | 'revenue'
  | 'expenses'
  | 'net_profit'
  | 'gross_profit'
  | 'profit_margin'
  | 'total_assets'
  | 'total_liabilities'
  | 'total_equity'
  | 'net_assets'
  | 'total_cash'
  | 'cash_on_hand'
  | 'bank_balance'
  | 'working_capital'
  | 'current_ratio'
  | 'debt_equity_ratio';

export type RatioType = 
  | 'current_ratio'
  | 'debt_equity_ratio'
  | 'gross_profit_margin'
  | 'net_profit_margin'
  | 'working_capital_ratio';

export interface WidgetConfig {
  // KPI
  metric?: KPIMetric;
  showTrend?: boolean;
  showSparkline?: boolean;
  
  // Account monitoring
  accountId?: string;
  accountCode?: string;
  accountName?: string;
  
  // Ratio
  ratio?: RatioType;
  benchmark?: number;
  
  // Chart
  chartType?: 'line' | 'bar' | 'area' | 'pie';
  dataSource?: string;
  period?: 'week' | 'month' | 'quarter' | 'year';
  
  // Common
  comparisonPeriod?: 'previous_period' | 'previous_year' | 'previous_month' | 'none';
  showBenchmark?: boolean;
  benchmarkValue?: number;
}

export interface DashboardWidget {
  id: string;
  type: WidgetType;
  title: string;
  size?: WidgetSize;
  position?: WidgetPosition;
  config: WidgetConfig;
  order?: number;
  isCollapsed?: boolean;
  isLocked?: boolean;
}

export interface DashboardLayout {
  id: string;
  name: string;
  description?: string;
  widgets: DashboardWidget[];
  columns?: number;
  rowHeight?: number;
  createdAt: string;
  updatedAt: string;
  isDefault?: boolean;
}

// Labels pour l'affichage
export const KPI_LABELS: Record<KPIMetric, { label: string; format: 'currency' | 'percent' | 'number' }> = {
  revenue: { label: 'Chiffre d\'affaires', format: 'currency' },
  expenses: { label: 'Charges', format: 'currency' },
  net_profit: { label: 'Résultat net', format: 'currency' },
  gross_profit: { label: 'Marge brute', format: 'currency' },
  profit_margin: { label: 'Marge nette', format: 'percent' },
  total_assets: { label: 'Total actifs', format: 'currency' },
  total_liabilities: { label: 'Total passifs', format: 'currency' },
  total_equity: { label: 'Capitaux propres', format: 'currency' },
  net_assets: { label: 'Actif net', format: 'currency' },
  total_cash: { label: 'Trésorerie', format: 'currency' },
  cash_on_hand: { label: 'Caisse', format: 'currency' },
  bank_balance: { label: 'Banque', format: 'currency' },
  working_capital: { label: 'BFR', format: 'currency' },
  current_ratio: { label: 'Ratio liquidité', format: 'number' },
  debt_equity_ratio: { label: 'Ratio endettement', format: 'number' }
};

export const RATIO_LABELS: Record<RatioType, { label: string; benchmark: number; unit: string }> = {
  current_ratio: { label: 'Ratio de liquidité', benchmark: 1.5, unit: '' },
  debt_equity_ratio: { label: 'Ratio d\'endettement', benchmark: 1.0, unit: '' },
  gross_profit_margin: { label: 'Marge brute', benchmark: 30, unit: '%' },
  net_profit_margin: { label: 'Marge nette', benchmark: 10, unit: '%' },
  working_capital_ratio: { label: 'BFR / CA', benchmark: 0.15, unit: '' }
};

// Classes SYSCOHADA
export const SYSCOHADA_CLASSES = [
  { code: '1', name: 'Ressources durables', type: 'equity' },
  { code: '2', name: 'Actif immobilisé', type: 'asset' },
  { code: '3', name: 'Stocks', type: 'asset' },
  { code: '4', name: 'Tiers', type: 'asset' },
  { code: '5', name: 'Trésorerie', type: 'asset' },
  { code: '6', name: 'Charges', type: 'expense' },
  { code: '7', name: 'Produits', type: 'revenue' },
  { code: '8', name: 'Autres charges', type: 'expense' },
  { code: '9', name: 'Analytique', type: 'expense' }
] as const;
